import * as Styles from "./QcConfigurationDrawerStyles";
import { useEffect, useState } from "react";
import { Button, Drawer, Form, Select } from "antd";
import { ReNotification } from "../../../../unity-ui-common/Components/ReNotification";
import ReInput from "../../../../unity-ui-common/Components/ReFormFields/ReInput";
import { tenant } from "../../../../API/UserManagement/Tenant";
import { auth } from "../../../../API/Auth";
import { saveState } from "../../../../sessionstorage";
import { handleSessionUpdate } from "../../../../slices/Session/Session";
import { useAppDispatch } from "../../../../app/hooks";

function QcConfigurationDrawer(props: {
  visibility: boolean;
  setVisibility: Function;
  currEditingSponor: any;
  setCurrEditingSponor: Function;
}) {
  const { visibility, setVisibility, currEditingSponor } = props;

  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const onFinish = async (values: any) => {
    if (!currEditingSponor?.id) {
      ReNotification({
        header: "Sponsor Configuration",
        description: "Sponsor information is missing",
        type: "error",
      });
      return;
    }
    setSubmitting(true);

    if (values.contactsToSend && typeof values.contactsToSend === "string") {
      values.contactsToSend = values.contactsToSend.split(",").map((i: string) => i.trim()).filter((i: string) => i !== "");
    } else if (!Array.isArray(values.contactsToSend)) {
      values.contactsToSend = [];
    }

    let res = await tenant.update(currEditingSponor.id, {
      configs: values,
    });
    res = await res?.json();
    if (res?.status) {
      // The PATCH response already carries the full, freshly-updated tenant
      // (with configs). Use it directly — no second network round-trip that
      // could fail or return a different shape.
      const updatedTenant = res?.data || {};
      const appliedMode =
        updatedTenant?.configs?.dataEntryMode ?? values?.dataEntryMode;

      // Is the edited sponsor the workspace the user is currently logged into?
      const workspace = (auth.getCurrentWorkspace() as any) || {};
      const isActiveWorkspace =
        !!workspace?.id &&
        String(workspace.id) === String(currEditingSponor?.id);

      if (isActiveWorkspace && updatedTenant?.id) {
        // Merge so we keep any session-only fields already on selectedTenant,
        // and update BOTH Redux and sessionStorage so every screen (side menu,
        // compliance tabs, etc.) reflects the new mode immediately.
        const merged = { ...workspace, ...updatedTenant };
        await saveState({ selectedTenant: merged });
        dispatch(handleSessionUpdate({ selectedTenant: merged }));
      }

      ReNotification({
        header: "Sponsor Configuration",
        description: isActiveWorkspace
          ? `Updated. Mode: ${appliedMode}. Applied to your current workspace.`
          : `Updated (mode: ${appliedMode}). This is NOT your active workspace — switch to this sponsor to see the menu change.`,
        type: "success",
        duration: 4000,
        placement: "bottomLeft",
      });
    } else {
      ReNotification({
        header: "Sponsor Configuration",
        description: "Sponsor is not updated",
        type: "error",
        duration: 1000,
        placement: "bottomLeft",
      });
    }
    setVisibility(false);

    setSubmitting(false);
    // dispatch(getAllSponsors());
  };

  const setInitialValuesOfForm = () => {
    const configs = currEditingSponor?.configs;
    form.setFieldsValue({
      numberOfDays: configs?.numberOfDays,
      contactsToSend: configs?.contactsToSend
        ? configs?.contactsToSend.join(",")
        : "",
      dataEntryMode: configs?.dataEntryMode || undefined,
    });
  };

  useEffect(() => {
    setInitialValuesOfForm();
  }, [currEditingSponor]);

  return (
    <div>
      <Drawer
        className="drawer"
        title="Configuration"
        width={600}
        placement="right"
        onClose={() => {
          setVisibility(false);
        }}
        open={visibility}
      >
        <Styles.Body>
          <Form
            className="form"
            form={form}
            layout="vertical"
            onFinish={onFinish}
            onFinishFailed={() => { }}
          >
            <ReInput
              min={0}
              required
              name="numberOfDays"
              type="number"
              label="Send reminder after (days)"
            />
            <ReInput
              required
              name="contactsToSend"
              type="textArea"
              label="Alert e-mail IDs"
            />
            <Form.Item
              name="dataEntryMode"
              label="Mode of Data Entry and Management"
            >
              <Select
                placeholder="Select mode"
                allowClear
                options={[
                  { label: "Centralized", value: "Centralized" },
                  { label: "Manual", value: "Manual" },
                  { label: "Hybrid", value: "Hybrid" },
                ]}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Submit
              </Button>
            </Form.Item>
          </Form>
        </Styles.Body>
      </Drawer>
    </div>
  );
}

export default QcConfigurationDrawer;
